import React, { useEffect, useRef, useState } from 'react';
import { colors, typography, borderRadius } from '../../styles/designTokens';
import machineImage from '../../assets/machine.webp';

const ProductCard: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const paragraphStyle: React.CSSProperties = {
    fontSize: 'clamp(1.05rem, 2.05vw, 1.3rem)',
    fontFamily: typography.body.fontFamily,
    background: 'linear-gradient(135deg, #1a1a1a 0%, #333333 50%, #1a1a1a 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: 1.55,
    margin: 0,
    fontWeight: 500,
    letterSpacing: '0.1px',
    wordSpacing: '0.15em',
    textShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
  };

  const descriptionStyle: React.CSSProperties = {
    fontSize: 'clamp(1.18rem, 2.35vw, 1.5rem)',
    fontFamily: typography.body.fontFamily,
    color: '#000000',
    fontWeight: 700,
    margin: 0,
    marginTop: 0,
    marginBottom: 'clamp(3px, 0.5vw, 6px)',
    lineHeight: 1.35,
    letterSpacing: '0.1px',
  };

  // ארגון התוכן - הפרדה בין תיאור שיווקי לנתונים טכניים
  const descriptionText = ' לחווית תספורת נעימה ונקיה יותר.';
  
  const technicalSpecs = [
    { label: 'דגם', value: 'IR13.' },
    { label: 'חומר גוף', value: 'פלסטיק חזק במיוחד.' },
    { label: 'מיכל איסוף', value: 'פלסטיק, נשלף בשיטת דחיפה.' },
    { label: 'חומר להבים', value: 'פלדה מושחזת איכותית.' },
    // { label: 'מגע הלהב', value: 'עדין במיוחד, מעניק תספורת חלקה ונוחה לעור בשילוב חדות אופטימלית לתוצאה מהירה ויעילה.' },
     { label: 'מגע הלהב', value: <>עדין במיוחד, מעניק תספורת חלקה ונוחה לעור<br />בשילוב חדות אופטימלית לתוצאה מהירה ויעילה.</> },
    // { label: 'מנוע', value: 'מערכת Dual-Motor  לביצוע 2 פעולות בו זמנית - חיתוך + שאיבה.' },
    { label: 'מנוע', value: <>מערכת Dual-Motor<br />לביצוע 2 פעולות בו זמנית - חיתוך + שאיבה.</> },
  ];

  const features: React.ReactNode[] = [];

  const accessories = [
    '4 מסרקי הגבהה (2 מ"מ, 3 מ"מ, 6 מ"מ, 9 מ"מ).',
    'מברשת לניקוי + בקבוקון שמן.',
    'ראש מטען + כבל הטענה באורך 2 מטר.',
  ];

  const additionalInfo = [
    { label: 'ניקיון', value: 'הלהב והמיכל ניתנים לפירוק בקלות לצורך ניקוי ותחזוקה.' },
    { label: 'זמן אספקה', value: '7 ימי עסקים בכפוף לתקנון המשלוחים.' },
    { label: 'אחריות', value: 'שנה בהתאם לתקנון.' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // כל פעם שהאלמנט נכנס או יוצא מהמסך, נעדכן את isVisible
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.2,
        rootMargin: '0px',
      }
    );

    const currentRef = cardRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <section
      ref={cardRef}
      id="product-specs"
      aria-label="כרטיס מוצר - פרטי מכונת AiRY"
      style={{
        width: '100%',
        marginTop: 'clamp(12px, 2vw, 30px)',
        marginBottom: 'clamp(20px, 3vw, 40px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0,
        position: 'relative',
      }}
    >
      {/* עטיפה משותפת לכפתור ולכרטיס - יכנסו ביחד */}
      <div
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateX(0) scale(1)' : 'translateX(50vw) scale(0.95)',
          transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
      {/* כפתור CTA - חלק מהכרטיס - עכשיו מיושר נכון */}
      <div className="flex flex-col items-center gap-4 waiting-text-container" style={{ 
        width: 'clamp(85%, 65vw, 50%)', 
        maxWidth: 'clamp(280px, 70vw, 600px)', 
        marginBottom: '-15px',
        position: 'relative',
        zIndex: 20,
      }}>
        <p 
          className="font-black text-center waiting-text" 
          style={{ 
            fontSize: 'clamp(18px, 5.5vw, 3rem)',
            background: colors.accentSpecial.textGradientRich,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: 0,
            marginBottom: 'clamp(2px, 0.5vw, 4px)',
            marginTop: '-20px',
          }}
        >
          אז למה אתם מחכים?
        </p>
        <a 
          href="/payment"
          className="group order-button-switch order-button-wave order-button-mobile rounded-full"
          aria-label="לחץ להצטרפות לחוויה ורכישת מכונת תספורת AiRY"
          style={{ 
            background: colors.accentSpecial.buttonGradientDiagonal,
            padding: 'clamp(10px, 1.5vw, 14px) clamp(50px, 8vw, 90px) clamp(10px, 1.5vw, 14px) clamp(60px, 9vw, 100px)',
            fontSize: 'clamp(18px, 2.5vw, 26px)',
            fontWeight: '700',
            color: '#000000',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginBottom: '0',
            marginTop: 'clamp(8px, 1.5vw, 16px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            zIndex: 20,
            cursor: 'pointer',
            animation: isVisible ? 'orderButtonColorSwitch 3s steps(1, end) infinite 1s' : 'none',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(255, 216, 153, 0.7), 0 0 40px rgba(252, 200, 122, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
          }}
        >
          <span>הצטרפו גם אתם לחוויה</span>
          
          {/* עיגול שחור עם חץ כתום - position absolute */}
          <div 
            className="flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-75 ease-out group-hover:-translate-x-2 order-button-circle"
            style={{
              position: 'absolute',
              left: '0px',
              height: '95%',
              aspectRatio: '1',
              backgroundColor: '#000000',
              border: '2px solid #D4943A',
            }}
          >
            {/* אייקון חץ */}
            <svg 
              className="transition-transform duration-75 ease-out rotate-45 group-hover:!rotate-0 order-button-arrow"
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              style={{
                width: 'clamp(26px, 4vw, 34px)',
                height: 'clamp(26px, 4vw, 34px)',
                color: colors.gold.base,
              }}
            >
              <path 
                d="M19 12H5M5 12L12 5M5 12L12 19" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </a>
      </div>

      {/* כרטיס המוצר - על כל הרוחב, עם פינות מעוגלות מכל הצדדים, הכפתור עליו */}
      <div
        className="product-card-container product-card-mobile"
        role="button"
        tabIndex={0}
        aria-label="מעבר לתשלום עבור מכונת התספורת AIRY"
        onClick={() => (window.location.href = '/payment')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            window.location.href = '/payment';
          }
        }}
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: borderRadius.soft, // פינות מעוגלות מכל הצדדים
          borderTopLeftRadius: borderRadius.soft, // פינות עליונות מעוגלות
          borderTopRightRadius: borderRadius.soft, // פינות עליונות מעוגלות
          padding: 'clamp(12px, 1.5vw, 14px) clamp(16px, 2vw, 18px)',
          paddingTop: 'clamp(12px, 1.5vw, 14px)',
          paddingBottom: 'clamp(12px, 1.5vw, 14px)',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
          display: 'grid',
          gridTemplateColumns: 'clamp(120px, 30%, 350px) 1fr',
          gap: 'clamp(8px, 1.2vw, 14px)',
          alignItems: 'center',
          direction: 'rtl',
          width: 'fit-content',
          maxWidth: 'clamp(320px, 85vw, 920px)',
          margin: '0 auto', // מרכז את הכרטיס
          marginTop: '0', // בלי margin שלילי - הכפתור כבר מיושר נכון
          position: 'relative',
          zIndex: 10, // מעל הפס
          cursor: 'pointer', // כל הכרטיס לחיץ
          overflow: 'visible' // מאפשר לפס להיראות מהצדדים
        }}
      >
        {/* תמונת המוצר */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <img
            src={machineImage}
            alt="מכונת תספורת AiRY בצבע שחור אפור עם מערכת שאיבה מובנית, כוללת 2 מנועים חזקים ולהב עדין"
            width="418"
            height="1150"
            loading="lazy"
            className="product-card-image"
            style={{
              width: '100%',
              maxWidth: '100%',
              height: 'auto',
              objectFit: 'contain',
              filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.12))', // צל עדין יותר
            }}
          />
        </div>

        {/* תוכן הכרטיס */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            paddingRight: 0, // אין padding מימין! צמוד למסגרת!
          }}
        >
          {/* כותרת ראשית - בולטת וגדולה */}
          <h2
            className="product-card-title"
            style={{
              fontSize: 'clamp(1.45rem, 3.3vw, 2.05rem)', // הגדלתי עוד! מ-1.3-1.85rem ל-1.45-2.05rem
              fontFamily: typography.heading.fontFamily,
              fontWeight: 800,
              background: 'linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 50%, #0d0d0d 100%)', // ניגודיות טובה יותר
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: 0,
              marginBottom: 0, // ללא ריווח מתחת לכותרת
              lineHeight: 1.25, // הגדלתי עוד!
              letterSpacing: '0.3px',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            AiRY - מכונת תספורת שואבת 
          </h2>

          {/* תיאור שיווקי - ממש מתחת לכותרת, שחור מודגש */}
          <p
            className="product-card-description"
            style={descriptionStyle}
          >
            {descriptionText}
          </p>

          {/* נתונים טכניים */}
          <div style={{ marginBottom: 'clamp(3px, 0.5vw, 6px)' }}> {/* מינימלי! */}
            {technicalSpecs.map((spec, index) => (
              <p
                key={index}
                style={{
                  ...paragraphStyle,
                  marginBottom: index === technicalSpecs.length - 1 ? 0 : 'clamp(2px, 0.4vw, 4px)', // ממש מינימלי!
                }}
              >
                <strong style={{ fontWeight: 700 }}>{spec.label}:</strong> {spec.value}
              </p>
            ))}
          </div>

          {/* תכונות */}
          <div style={{ marginBottom: 'clamp(3px, 0.5vw, 6px)' }}> {/* מינימלי! */}
            {features.map((feature, index) => (
              <p
                key={index}
                style={{
                  ...paragraphStyle,
                  marginBottom: index === features.length - 1 ? 0 : 'clamp(2px, 0.4vw, 4px)', // ממש מינימלי!
                }}
              >
                {feature}
              </p>
            ))}
          </div>

          {/* אביזרים נלווים - כל אחד בשורה נפרדת */}
          <div style={{ marginBottom: 'clamp(3px, 0.5vw, 6px)' }}> {/* מינימלי! */}
            <p style={{ ...paragraphStyle, marginBottom: 'clamp(2px, 0.4vw, 4px)' }}> {/* מינימלי! */}
              <strong style={{ fontWeight: 700 }}>אביזרים נלווים:</strong>
            </p>
            {accessories.map((accessory, index) => (
              <p
                key={index}
                style={{
                  ...paragraphStyle,
                  marginBottom: index === accessories.length - 1 ? 0 : 'clamp(2px, 0.4vw, 4px)', // ממש מינימלי!
                  paddingRight: 0, // ללא הזחה - הנקודות מתחילות באותו מיקום כמו "אביזרים נלווים:"
                }}
              >
                • {accessory}
              </p>
            ))}
          </div>

          {/* מידע נוסף */}
          <div>
            {additionalInfo.map((info, index) => (
              <p
                key={index}
                style={{
                  ...paragraphStyle,
                  marginBottom: index === additionalInfo.length - 1 ? 0 : 'clamp(2px, 0.4vw, 4px)', // ממש מינימלי!
                }}
              >
                <strong style={{ fontWeight: 700 }}>{info.label}:</strong> {info.value}
              </p>
            ))}
          </div>
        </div>
      </div>

      {/* פס רץ כשכבת רקע מאחורי הכרטיס */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: isVisible ? 'translate(-50%, -50%)' : 'translate(calc(-50% + 50vw), -50%)',
          width: '120%',
          height: '12px',
          zIndex: 0,
          overflow: 'visible',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            width: '60%',
            height: '100%',
            background: `linear-gradient(90deg, 
              transparent 0%, 
              ${colors.gold.darker} 5%,
              ${colors.gold.dark} 12%, 
              ${colors.gold.mediumDark} 20%,
              #FFFFFF 28%,
              ${colors.gold.base} 38%, 
              #FFFFFF 48%,
              ${colors.gold.mediumLight} 58%,
              ${colors.gold.light} 68%,
              #FFFFFF 78%,
              ${colors.gold.lighter} 88%,
              ${colors.gold.light} 95%,
              transparent 100%
            )`,
            borderRadius: '50px',
            clipPath: 'polygon(0% 50%, 8% 0%, 92% 30%, 100% 50%, 92% 70%, 8% 100%)',
            animation: isVisible ? 'waveGlow 2.5s ease-in-out 1 forwards' : 'none',
            boxShadow: `
              0 0 20px rgba(245, 182, 88, 0.8),
              0 0 35px rgba(245, 182, 88, 0.5),
              0 0 50px rgba(255, 255, 255, 0.4)
            `,
            filter: 'brightness(1.2)',
          }}
        />
      </div>
      </div>
      {/* סיום העטיפה המשותפת */}
    </section>
  );
};

export default ProductCard;

